import { isFirefox } from '@/utils/browser-kit';
import { useTextEditState } from '../states/text-edit-state';
import { deleteTextRange, findPreviousTextNode, isCursorAtEnd } from '../utils';

type KeyboardHandler = (event: React.KeyboardEvent<HTMLDivElement>) => void;

export const useKeyboardEvent = () => {
    const exitEditing = useTextEditState()((state) => state.exitEditing);
    const getEditingLayer = useTextEditState()((state) => state.getEditingLayer);

    const handlerForEnter: KeyboardHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
        event.preventDefault();

        if (Reflect.get(event.target, 'composing')) return;

        if (isFirefox) {
            document.execCommand('insertText', false, '\n');
            return;
        }

        const currentTarget = event.currentTarget as HTMLDivElement;
        const isEnd = isCursorAtEnd(currentTarget);

        const isEndWithNewline = currentTarget.textContent?.endsWith('\n');
        const newLineChar = isEnd && !isEndWithNewline ? '\n\n' : '\n';

        document.execCommand('insertHTML', false, newLineChar);
    };

    const handleForTab: KeyboardHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
        event.preventDefault();

        const mockTabChar = '\u3000\u3000';
        document.execCommand('insertText', false, mockTabChar);
    };

    const handleForBackspace: KeyboardHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const currentTarget = event.currentTarget as HTMLDivElement;

        const range = selection.getRangeAt(0);
        const startContainer = range.startContainer;
        const offset = range.startOffset;

        // 删除连续的两个全角空格
        if (startContainer.nodeType === Node.TEXT_NODE) {
            if (offset === 0) {
                const previousTextNode = findPreviousTextNode(startContainer, currentTarget);
                if (previousTextNode && previousTextNode.textContent?.endsWith('\u3000\u3000')) {
                    const length = previousTextNode.textContent.length;

                    deleteTextRange(previousTextNode, length - 2, length);
                    event.preventDefault();
                }
            } else {
                const text = startContainer.textContent;
                const isValidIndex = offset - 2 >= 0;

                if (text && isValidIndex && text[offset - 1] === '\u3000' && text[offset - 2] === '\u3000') {
                    deleteTextRange(startContainer, offset - 2, offset);
                    event.preventDefault();
                }
            }
        }
    };

    const handlerForEscape: KeyboardHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
        event.preventDefault();

        const editingLayer = getEditingLayer();
        if (editingLayer) {
            return exitEditing();
        }
    };

    const handlers: Record<string, KeyboardHandler> = {
        Enter: handlerForEnter,
        Tab: handleForTab,
        Backspace: handleForBackspace,
        Escape: handlerForEscape,
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const key = event.key;
        const handler = handlers[key];
        if (handler) handler(event);
    };

    return {
        handleKeyDown,
    };
};
